import { Component } from '@theme/component';

const MIN_REFERENCE_WIDTH = 1400;
const MAX_SETS_PER_HALF = 20;

/**
 * Announcement banner custom element that creates a seamless infinite marquee.
 * Only one set of announcements is rendered in Liquid; this clones the set
 * (aria-hidden) until it fills the bar, then doubles it so the track can
 * loop at -50%.
 *
 * @extends {Component}
 */
export class AnnouncementBar extends Component {
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  #lastWidth = 0;
  #lastSetWidth = 0;
  #frame = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#fill();
    // Web fonts change the text width once loaded
    document.fonts?.ready.then(() => this.#fill());

    // Refill when the bar or the original set changes width (fonts, late styles)
    this.#resizeObserver = new ResizeObserver(() => {
      const original = this.#original;
      if (this.offsetWidth === this.#lastWidth && original?.offsetWidth === this.#lastSetWidth) return;
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#fill());
    });
    this.#resizeObserver.observe(this);
    if (this.#original) this.#resizeObserver.observe(this.#original);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  get #original() {
    return this.querySelector('.announcement-bar__content:not([data-clone])');
  }

  #fill() {
    const track = this.querySelector('.announcement-bar__track');
    const original = this.#original;
    if (!(track instanceof HTMLElement) || !(original instanceof HTMLElement)) return;

    track.querySelectorAll('[data-clone]').forEach((clone) => clone.remove());

    const containerWidth = this.offsetWidth;
    const setWidth = original.offsetWidth;
    this.#lastWidth = containerWidth;
    this.#lastSetWidth = setWidth;
    if (!containerWidth || !setWidth) return;

    // One half of the track must be at least as wide as the bar. Use the
    // viewport as an upper bound so a bogus measurement can't explode the track.
    const barWidth = Math.min(containerWidth, document.documentElement.clientWidth || containerWidth);
    const setsPerHalf = Math.min(MAX_SETS_PER_HALF, Math.max(1, Math.ceil(barWidth / setWidth)));
    const totalSets = setsPerHalf * 2;

    const fragment = document.createDocumentFragment();
    for (let i = 1; i < totalSets; i++) {
      fragment.append(createClone(original));
    }
    track.append(fragment);

    // `speed` seconds to scroll one bar width, measured against at least
    // MIN_REFERENCE_WIDTH so narrow (mobile) bars don't crawl
    const speed = parseFloat(this.getAttribute('speed') || '20');
    const referenceWidth = Math.max(barWidth, MIN_REFERENCE_WIDTH);
    // Use the rendered half-track so the speed stays right even if a set's
    // width differs from the original measurement
    const halfWidth = track.scrollWidth / 2 || setWidth * setsPerHalf;
    const duration = (speed * halfWidth) / referenceWidth;
    track.style.setProperty('--marquee-duration', `${duration}s`);
  }
}

/**
 * @param {HTMLElement} element
 * @returns {HTMLElement}
 */
function createClone(element) {
  const clone = /** @type {HTMLElement} */ (element.cloneNode(true));
  clone.setAttribute('aria-hidden', 'true');
  clone.setAttribute('inert', '');
  clone.dataset.clone = '';
  clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
  clone.querySelectorAll('[data-shopify-editor-block]').forEach((el) => el.removeAttribute('data-shopify-editor-block'));
  return clone;
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
